import Hotjar from "@hotjar/browser";
import VueGtag from "vue-gtag";
import {
  hasAnalyticsConsent,
  isAnalyticsFeatureEnabled
} from "@/services/consentService";
import { logDebugEvent } from "@/services/debugLogs";

const DEFAULT_GA_MEASUREMENT_ID = "G-8XJMW8N6QS";
const DEFAULT_HOTJAR_SITE_ID = 3665917;
const DEFAULT_HOTJAR_VERSION = 6;

const GA_MEASUREMENT_ID =
  process.env.VUE_APP_GA_MEASUREMENT_ID || DEFAULT_GA_MEASUREMENT_ID;
const HOTJAR_SITE_ID = Number(
  process.env.VUE_APP_HOTJAR_SITE_ID || DEFAULT_HOTJAR_SITE_ID
);
const HOTJAR_VERSION = Number(
  process.env.VUE_APP_HOTJAR_VERSION || DEFAULT_HOTJAR_VERSION
);

let appInstance = null;
let analyticsInitialized = false;
let watcherAttached = false;

function initGoogleAnalytics() {
  if (!GA_MEASUREMENT_ID || !appInstance) {
    return;
  }

  appInstance.use(VueGtag, {
    config: { id: GA_MEASUREMENT_ID }
  });
}

function initHotjar() {
  if (!HOTJAR_SITE_ID || !HOTJAR_VERSION) {
    return;
  }

  const debugOptions =
    process.env.NODE_ENV !== "production" ? { debug: true } : undefined;
  Hotjar.init(HOTJAR_SITE_ID, HOTJAR_VERSION, debugOptions);
}

export function registerAnalyticsApp(app) {
  appInstance = app;
}

export function initAnalyticsIfConsented() {
  if (!appInstance || analyticsInitialized) {
    return false;
  }

  if (!isAnalyticsFeatureEnabled() || !hasAnalyticsConsent()) {
    return false;
  }

  initGoogleAnalytics();
  initHotjar();
  analyticsInitialized = true;

  logDebugEvent("analytics", "initialized", {
    analyticsEnabled: true,
    gaConfigured: Boolean(GA_MEASUREMENT_ID),
    hotjarConfigured: Boolean(HOTJAR_SITE_ID && HOTJAR_VERSION)
  });

  return true;
}

export function startAnalyticsConsentWatcher() {
  if (watcherAttached || typeof window === "undefined") {
    return;
  }

  watcherAttached = true;
  window.addEventListener("engram:consent-updated", () => {
    initAnalyticsIfConsented();
  });
}

// ============================================================================
// SHARED ANALYTICS HELPER
// All event tracking in the app should go through trackEvent().
// This ensures consent is always checked before a GA event fires,
// and keeps all event calls in one place for easy maintenance.
// ============================================================================

/**
 * Fire a GA4 event if analytics consent has been granted.
 * @param {string} eventName  snake_case event name
 * @param {object} [params]   GA4 event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!isAnalyticsFeatureEnabled() || !hasAnalyticsConsent()) {
    return;
  }

  const isLocalhost =
    typeof window !== "undefined" && window.location.hostname === "localhost";
  const eventParams = isLocalhost ? { ...params, debug_mode: true } : params;

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, eventParams);
  }
}

/**
 * Fire a GA4 page_view event on route changes.
 * @param {string} pagePath   e.g. "/ap-world/period/1"
 * @param {string} pageTitle  document.title at the time of navigation
 */
export function trackPageView(pagePath, pageTitle) {
  trackEvent("page_view", {
    page_path: pagePath,
    page_title: pageTitle || document.title
  });
}

export function getAnalyticsDebugStatus() {
  const isClient = typeof window !== "undefined";
  const consentRaw = isClient
    ? window.localStorage.getItem("engram_cookie_consent")
    : null;

  return {
    gaMeasurementId: GA_MEASUREMENT_ID,
    analyticsFeatureEnabled: isAnalyticsFeatureEnabled(),
    hasAnalyticsConsent: hasAnalyticsConsent(),
    analyticsInitialized,
    gtagAvailable:
      isClient && typeof window.gtag === "function",
    hostname: isClient ? window.location.hostname : null,
    consentRaw
  };
}

export function sendAnalyticsDebugTestEvent() {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return false;
  }

  if (!isAnalyticsFeatureEnabled() || !hasAnalyticsConsent()) {
    return false;
  }

  window.gtag("event", "ga_debug_manual_test", {
    debug_mode: true,
    event_origin: "engram_debug_helper",
    event_timestamp: Date.now()
  });

  return true;
}
